import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import LaunchIcon from "@mui/icons-material/Launch";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import ErrorIcon from "@mui/icons-material/Error";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import tsx from "react-syntax-highlighter/dist/cjs/languages/prism/tsx";
import typescript from "react-syntax-highlighter/dist/cjs/languages/prism/typescript";

import { MarkdownRenderer } from "../markdown/MarkdownRenderer";
import { ConversationMessage, Source } from "../../types";
import { useRateMessage } from "../../hooks/useRateMessage";
import { useAppState } from "../../state/App";
import { CopyToClipboardButton } from "../common/CopyToClipboardButton";
import { RatingDialog } from "./RatingDialog";
import { defineMessages, MessageDescriptor, useIntl } from "react-intl";
import { commonMessages } from "../../commonMessages";
import {
  defaultMode,
  noInnioMode,
  innioTrainingsMode,
  researcherMode,
  companyMode,
} from "../../hooks/useModes";
import { useConversationCopy } from "../../hooks/useConversationCopy";

SyntaxHighlighter.registerLanguage("tsx", tsx);
SyntaxHighlighter.registerLanguage("typescript", typescript);

type Props = {
  isGenerating?: boolean;
  answer: ConversationMessage;
};

const messages = defineMessages({
  callingFunction: {
    id: "answer.callingFunction",
    defaultMessage: "Calling function ...",
  },
  reasoning: {
    id: "answer.reasoning",
    defaultMessage: "AI Internal Thoughts",
  },
  header: {
    id: "answer.header",
    defaultMessage: "AI-generated content may be incorrect",
  },
  thinking: {
    id: "answer.thinking",
    defaultMessage: "Let me think...",
  },
  webSearch: {
    id: "answer.function.web_search",
    defaultMessage: "Searching the web...",
  },
  openPage: {
    id: "answer.function.open_url",
    defaultMessage: "Visiting the website ...",
  },
  searchDocuments: {
    id: "answer.function.search_documents",
    defaultMessage: "Searching documents...",
  },
  attachmentAccess: {
    id: "answer.function.attachment_access",
    defaultMessage: "Reading attachments...",
  },
  imageGeneration: {
    id: "answer.function.image_generation",
    defaultMessage: "Generating images...",
  },
  pythonCodeInterpreter: {
    id: "answer.function.python_code_interpreter",
    defaultMessage: "Running Python code...",
  },
  audioAnalysis: {
    id: "answer.function.audio_analysis",
    defaultMessage: "Analyzing audio...",
  },
  videoAnalysis: {
    id: "answer.function.video_analysis",
    defaultMessage: "Analyzing video...",
  },
  newConversation: {
    id: "answer.newConversation",
    defaultMessage: "Start new from here",
  },
});

const functionMessages: Record<string, MessageDescriptor> = {
  web_search: messages.webSearch,
  open_url: messages.openPage,
  python_code_interpreter: messages.pythonCodeInterpreter,
  audio_analysis: messages.audioAnalysis,
  video_analysis: messages.videoAnalysis,
  attachment_access: messages.attachmentAccess,
};

function makeTTSUrl({
  conversationId,
  messageId,
}: {
  conversationId?: string;
  messageId?: string;
}) {
  if (!conversationId || !messageId) {
    return "";
  }
  return `/api/history/conversation/${conversationId}/message/${messageId}/text-to-speech`;
}

export const Answer = ({ isGenerating, answer }: Props) => {
  const text = answer.content;

  const showSkeleton = isGenerating && !text;

  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardContent
        sx={{
          pt: 0,
          pb: "0 !important",
        }}
      >
        <AnswerHeader answer={answer} />

        {!showSkeleton && text && (
          <>
            <div className="flex-grow py-4">
              <MemoizedMDRenderer markdown={text ?? "&nbsp;"} />
            </div>
          </>
        )}
        {answer.reasoning_content && (
          <ReasoningSection
            reasoning={answer.reasoning_content}
            isGenerating={isGenerating && !text}
          />
        )}
        {showSkeleton && <ResponseAnimation answer={answer} />}
        <AnswerActions answer={answer} />
      </CardContent>
    </Card>
  );
};

const MemoizedMDRenderer = React.memo(MarkdownRenderer);

function ReasoningSection({
  reasoning,
  isGenerating,
}: {
  reasoning: string;
  isGenerating?: boolean;
}) {
  const { formatMessage: t } = useIntl();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (isGenerating) {
      setExpanded(true);
    }
    else {
      setExpanded(false);
    }
  }, [isGenerating]);

  return (
    <div className="my-2">
      <Accordion
        disableGutters
        elevation={0}
        square
        expanded={expanded}
        onChange={(_e, v) => setExpanded(v)}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          "&::before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
          sx={{
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,.05)"
                : "rgba(0,0,0,.03)",
            flexDirection: "row-reverse",
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              transform: "rotate(90deg)",
            },
            "& .MuiAccordionSummary-content": { ml: 1 },
          }}
        >
          <Typography variant="caption" color="text.secondary" fontStyle="italic">
            {t(messages.reasoning)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              "& p, & li, & td, & th, & blockquote": {
                fontSize: "0.8rem",
                color: "text.secondary",
              },
              "& h1, & h2, & h3, & h4, & h5, & h6": {
                color: "text.secondary",
                marginTop: "0.75rem",
                marginBottom: "0.25rem",
                "&:first-child": { marginTop: 0 },
              },
            }}
          >
            <MemoizedMDRenderer markdown={reasoning} />
          </Box>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

export const AnswerHeader = ({ answer }: Props) => {
  const { formatMessage: t } = useIntl();
  const theme = useTheme();
  const canRate = answer._id && answer.conversation_id && answer.rating !== -2;
  const {
    state: { currentTTSUrl, isPlaying, isGenerating },
    actions: {
      updateMessage,
      setCurrentTTSUrl,
      setIsPlaying,
      setCurrentConversationId,
      setMessages,
    },
  } = useAppState();
  const { mutateAsync: rateMessage, isLoading } = useRateMessage();
  const { mutateAsync: copyConversation, isLoading: isCopying } =
    useConversationCopy();
  const [currentRating, setCurrentRating] = useState<number | undefined>(
    undefined
  );
  const isRatingDialogOpen =
    typeof currentRating !== "undefined" && currentRating !== 0;

  const ttsUrl = makeTTSUrl({
    conversationId: answer.conversation_id,
    messageId: answer._id,
  });

  const isPlayingAudio = isPlaying && currentTTSUrl === ttsUrl;
  const isCallingFunction = answer.calling_function;

  const rate = async (_rating: number, comment?: string) => {
    if (!answer._id) {
      return;
    }
    const rating = answer.rating === _rating ? 0 : _rating;
    await rateMessage({
      conversationId: answer.conversation_id,
      messageId: answer._id,
      rating,
      comment,
    });
    updateMessage({
      ...answer,
      rating,
      rating_comment: comment,
    });
  };

  const getModeName = (name: string) => {
    if (name === defaultMode) {
      return t(commonMessages.defaultMode);
    }
    if (name === noInnioMode) {
      return t(commonMessages.noInnioMode);
    }
    if (name === innioTrainingsMode) {
      return t(commonMessages.innioTrainingsMode);
    }
    if (name === researcherMode) {
      return t(commonMessages.researcherMode);
    }
    if (name === companyMode) {
      return t(commonMessages.companyMode);
    }
    return name;
  };

  const startRating = (_rating: number) => {
    if (answer.rating === _rating) {
      rate(0);
      return;
    }
    setCurrentRating(_rating);
  };
  const closeRatingDialog = () => {
    setCurrentRating(undefined);
  };

  const startAudioStream = () => {
    if (!answer._id || !answer.conversation_id) {
      return;
    }
    setCurrentTTSUrl(ttsUrl);
    setIsPlaying(true);
  };

  const stopAudioStream = () => {
    setIsPlaying(false);
  };

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleStartNewConversation = async () => {
    setMenuAnchor(null);
    if (!answer.conversation_id || !answer._id) return;
    const newConversation = await copyConversation({
      conversationId: answer.conversation_id,
      upToMessageId: answer._id,
    });
    setMessages([]);
    setCurrentConversationId(newConversation._id);
  };

  return (
    <>
      <RatingDialog
        open={isRatingDialogOpen}
        onClose={closeRatingDialog}
        onSubmit={(comment) => {
          rate(currentRating || 0, comment);
          closeRatingDialog();
        }}
      />
      <CardHeader
        sx={{
          pb: "0 !important",
          px: "0 !important",
          "&": {
            flexWrap: "wrap !important",
            ".MuiCardHeader-content": {
              pb: 2,
            },
            ".MuiCardHeader-action": {
              flexGrow: 1,
              maxWidth: "100%",
            },
          },
        }}
        title={
          <Typography
            variant="caption"
            color={theme.palette.action.disabled}
            sx={{
              mr: 2,
            }}
          >
            {t(messages.header)}
          </Typography>
        }
        action={
          <div className="flex justify-end items-center">
            {answer.mode_name && (
              <Chip
                className="overflow-y-hidden"
                label={getModeName(answer.mode_name)}
                size="small"
                variant="outlined"
                color="secondary"
              ></Chip>
            )}
            {!answer.mode_name && answer.no_innio_context && (
              <Chip
                label="Disabled Company Content"
                size="small"
                variant="outlined"
                color="warning"
              ></Chip>
            )}
            <Tooltip
              title={
                isCallingFunction
                  ? t(messages.callingFunction)
                  : answer.function_content?.replaceAll("|", ", ")
              }
              enterTouchDelay={0}
              leaveTouchDelay={3000}
            >
              <span>
                <IconButton
                  aria-label="settings"
                  size="small"
                  disabled={!answer.function_content}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Play">
              <span>
                <IconButton
                  aria-label="settings"
                  size="small"
                  disabled={!answer.conversation_id || !answer._id}
                  onClick={() =>
                    isPlayingAudio ? stopAudioStream() : startAudioStream()
                  }
                >
                  {isPlayingAudio ? (
                    <StopCircleIcon fontSize="small" />
                  ) : (
                    <PlayCircleOutlineIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <CopyToClipboardButton
              size="small"
              text={answer.content}
              disabled={!answer.content}
            />
            <Tooltip title={answer.rating === 1 && answer.rating_comment}>
              <span>
                <IconButton
                  aria-label="settings"
                  size="small"
                  disabled={!canRate || isLoading}
                  onClick={startRating.bind(null, 1)}
                >
                  {answer.rating === 1 ? (
                    <ThumbUpIcon fontSize="small" />
                  ) : (
                    <ThumbUpAltOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={answer.rating === -1 && answer.rating_comment}>
              <span>
                <IconButton
                  aria-label="rating"
                  size="small"
                  disabled={!canRate || isLoading}
                  onClick={startRating.bind(null, -1)}
                >
                  {answer.rating === -1 ? (
                    <ThumbDownIcon fontSize="small" />
                  ) : (
                    <ThumbDownOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              disabled={
                isGenerating ||
                isCopying ||
                !answer.conversation_id ||
                !answer._id
              }
              aria-label="more options"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem
                onClick={handleStartNewConversation}
                disabled={isCopying}
              >
                <AltRouteIcon fontSize="small" sx={{ mr: 1 }} />
                {t(messages.newConversation)}
              </MenuItem>
            </Menu>
          </div>
        }
      />
    </>
  );
};

type SourceChunked = {
  id: string;
  name: string;
  chunks: Source[];
  has_source_link: boolean;
};

function ResponseAnimation({ answer }: Props) {
  const { formatMessage: t } = useIntl();
  const theme = useTheme();

  let displayText;
  if (answer.function_name) {
    const functionName = answer.function_name.trim();

    let messageKey;
    if (functionName.includes("image")) {
      messageKey = messages.imageGeneration;
    } else if (functionName.startsWith("search_documents")) {
      messageKey = messages.searchDocuments;
    } else {
      messageKey = functionMessages[functionName];
    }

    displayText = messageKey ? t(messageKey) : t(messages.thinking);
  } else {
    displayText = t(messages.thinking);
  }

  const chars = displayText.split("");
  return (
    <div className="whitespace-pre-line pb-4">
      <Typography variant="h6" sx={{ fontStyle: "italic" }}>
        {chars.map((char, index) => (
          <span
            key={index}
            style={{
              display: "inline-block",
              color: theme.palette.text.secondary,
              animation: `fade-char 3s ease-in-out infinite`,
              animationDelay: `${index * 0.1}s`,
            }}
          >
            {char === " " ? "\u00A0" : char}
          </span>
        ))}
      </Typography>
      <style>
        {`
          @keyframes fade-char {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.3;
            }
          }
        `}
      </style>
    </div>
  );
}

function AnswerActions({ answer }: Props) {
  const [selectedDocumentId, setSelectedDocumentId] = useState<
    string | undefined
  >(undefined);

  const [anchorEl, setAnchorEl] = useState<HTMLAnchorElement | null>(null);

  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    documentId: string
  ) => {
    setSelectedDocumentId(String(documentId));
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedDocumentId(undefined);
  };

  const sourcesGrouped = answer.sources?.reduce<Record<string, SourceChunked>>(
    (acc, source) => {
      const key = String(source.document_id);
      if (!acc[key]) {
        acc[key] = {
          id: key,
          name: source.name,
          chunks: [],
          has_source_link: source.has_source_link,
        };
      }
      acc[key].chunks = [...acc[key].chunks, source];
      return acc;
    },
    {}
  );

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const isSourceSelected = typeof selectedDocumentId !== "undefined";
  const selectedSourceChunks = isSourceSelected
    ? sourcesGrouped?.[selectedDocumentId]
    : null;

  if (!answer.sources?.length) return null;

  return (
    <>
      <Divider />
      <div className="my-2 flex flex-wrap flex-grow items-center">
        <List dense>
          {Object.entries(sourcesGrouped || []).map((source, idx) => (
            <Link
              href=""
              target="_blank"
              rel="noopener"
              underline="none"
              key={idx}
              onClick={(e) => {
                e.preventDefault();
                handleClick(e, source[0]);
              }}
            >
              <ListItem>
                <ListItemIcon>
                  <DescriptionIcon />
                </ListItemIcon>
                <ListItemText primary={source[1].name} />
              </ListItem>
            </Link>
          ))}
        </List>
      </div>
      <Popover
        id={id}
        open={open && isSourceSelected}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx(theme) {
              return {
                [theme.breakpoints.up("md")]: {
                  maxWidth: "42rem",
                  maxHeight: "28rem",
                },
              };
            },
          },
        }}
      >
        <div className="m-4">
          {selectedSourceChunks?.has_source_link ? (
            <Link
              href={`/api/history/conversation/${answer.conversation_id}/message/${answer._id}/document/${selectedSourceChunks?.id}`}
              target="_blank"
              rel="noopener"
              underline="none"
            >
              <Typography variant="h6" className="mb-4 flex items-center">
                <LaunchIcon className="mr-2" />
                {selectedSourceChunks?.name || "Source"}
              </Typography>
            </Link>
          ) : (
            <Typography variant="h6" className="mb-4 flex items-center">
              {selectedSourceChunks?.name || "Source"}
            </Typography>
          )}

          {selectedSourceChunks?.chunks.map((chunk, idx) => (
            <div key={idx}>
              <div>
                <Typography className="whitespace-pre-line">
                  {chunk.content}
                </Typography>
              </div>
              <Divider className="my-8" />
            </div>
          ))}
        </div>
      </Popover>
    </>
  );
}

export const ErrorAnswer = ({ answer }: Props) => {
  return (
    <Card variant="outlined">
      <CardContent className="!pb-4 max-h-80 overflow-y-auto">
        <div className="flex items-center">
          <div className="mr-2">
            <ErrorIcon color="warning" />
          </div>
          <div className="flex flex-col">
            <Typography className="mb-2">Error</Typography>
            <Typography variant="body2">{answer.content}</Typography>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
